import { CarCategoryFindDto, type CarCategoryFindInterface } from '@zaoblako/shared/modules/cctv/dtos/car-category/car-category-find.dto';
import { CctvDtoKeysEnum } from '@zaoblako/shared/modules/cctv/enums/cctv-dto-keys.enum';
import { TransitTaskStatusEnum } from '@zaoblako/shared/modules/cctv/enums/transit-task/transit-task-status.enum';
import { ClientFindDto, type ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { ThreadFindInterface } from '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';
import { MessengerDtoKeysEnum } from '@zaoblako/shared/modules/messenger/enums/messenger-dto-keys.enum';
import { ExternalUserFindDto, type ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import { UserGroupFindDto, type UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { RoomFindDto, type RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';

import * as yup from 'yup';

/** Transit task response body */
export interface TransitTaskFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Created date */
  created: Date;
  /** Deleted date */
  deleted?: Date;
  /** Transit day */
  date: Date;
  /** State registration number of the car */
  carNumber: string;
  /** Car category */
  category: CarCategoryFindInterface;
  /** The room the car drove into */
  residenceRoom?: RoomFindInterface;
  /** Entry date */
  residenceEntry?: Date;
  /** Departure date */
  residenceDeparture?: Date;
  /** Time of actual stay (minutes) */
  residenceTotalTime?: number;
  /** Cost of stay (number of hours of stay * tariff) */
  residenceAmount?: number;
  /** Transit task status */
  status: TransitTaskStatusEnum;
  /** Client */
  client?: ClientFindInterface;
  /** Thread this comment belongs to */
  thread?: ThreadFindInterface;
  /** Responsible (internal user) */
  responsible?: UserFindInterface;
  /** External user */
  externalUser?: ExternalUserFindInterface;
  /** User who created this transit task */
  createdBy?: UserFindInterface;
  /** Auditors (internal users) */
  userAuditors: UserFindInterface[];
  /** Auditor groups (internal users) */
  userGroupAuditors: UserGroupFindInterface[];
  /** Rooms to which access is granted */
  rooms?: RoomFindInterface[];
  /** Count files in current transit task */
  filesCount?: number;
}

/** Transit task response body - DTO schema */
export const TransitTaskFindDto = SerdeDtoClass.create<TransitTaskFindInterface>(CctvDtoKeysEnum.TRANSIT_TASK_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Дата создания',
    serializer: yup.date(),
  },
  deleted: {
    label: 'Дата удаления',
    serializer: yup.date(),
  },
  date: {
    sortable: true,
    label: 'Дата проезда',
    serializer: yup.date(),
  },
  carNumber: {
    sortable: true,
    label: 'Номер ТС',
    serializer: yup.string(),
  },
  status: {
    sortable: true,
    label: 'Статус',
    serializer: yup
      .string()
      .oneOf(Object.values(TransitTaskStatusEnum)),
  },
  responsible: {
    sortable: true,
    label: 'Ответственный',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  externalUser: {
    sortable: true,
    label: 'Внешний пользователь',
    nested: ExternalUserFindDto,
    serializer: (_, nested) => nested,
  },
  createdBy: {
    sortable: true,
    label: 'Создатель заявки на проезд',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  client: {
    sortable: true,
    label: 'Клиент',
    nested: ClientFindDto,
    serializer: (_, nested) => nested,
  },
  thread: {
    optional: true,
    label: 'Ветка комментариев',
    nested: MessengerDtoKeysEnum.THREAD_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  category: {
    sortable: true,
    label: 'Категория ТС',
    nested: CarCategoryFindDto,
    serializer: (_, nested) => nested,
  },
  residenceRoom: {
    sortable: true,
    optional: true,
    label: 'Место въезда',
    nested: RoomFindDto,
    serializer: (_, nested) => nested,
  },
  residenceEntry: {
    sortable: true,
    optional: true,
    label: 'Дата въезда',
    serializer: yup.date(),
  },
  residenceDeparture: {
    sortable: true,
    optional: true,
    label: 'Дата выезда',
    serializer: yup.date(),
  },
  residenceTotalTime: {
    sortable: true,
    optional: true,
    label: 'Итоговое время нахождения (в секундах)',
    serializer: yup.number(),
  },
  residenceAmount: {
    sortable: true,
    optional: true,
    label: 'Сумма (количество часов пребывания * тариф)',
    serializer: yup.number(),
  },
  userAuditors: {
    label: 'Аудиторы',
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  userGroupAuditors: {
    label: 'Группы аудиторов',
    nested: UserGroupFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  rooms: {
    optional: true,
    label: 'Помещения',
    nested: RoomFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  filesCount: {
    label: 'Количество файлов в заявке',
    serializer: yup.number(),
  },
});
